import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
    ColumnChooser,
    ColumnChooserSearch,
    ColumnFixing,
    DataGrid,
    Export,
    FilterPanel,
    FilterRow,
    GroupPanel,
    Grouping,
    Item,
    Pager,
    Paging,
    Scrolling,
    Selection,
    Sorting,
    StateStoring,
    Toolbar,
} from "devextreme-react/data-grid";
import { GridLayout } from "../../../common/GridLayout";
import GridLayoutManager from "../../../gridLayoutCustomization/GridLayoutManager";
import { ALLOWED_PAGE_SIZES, TRIP_STATUS_OPTIONS } from "../constants";
import { TOP_BOARD_COLUMNS } from "./DispatchObjectColumns";
import { renderColumns } from "./GridColumns";
import { buildOnCellPrepared } from "./RowColoring";

/**
 * The Current tab's own column, appended to the shared set for that tab alone.
 *
 * Only uFMM0058 selects TRIPSTATUS, so offering it on Pick and Del would give those two choosers an
 * entry that is always blank. Coded field, so it renders as a descriptive lookup (Architecture.md
 * rule 3); the codes are uFMM0058's own Codes/Items pair, and 'P' is also what this tab's colouring
 * keys on.
 */
const TRIP_STATUS_COLUMN = {
    dataField: "TripStatus",
    caption: "Trip Status",
    lookup: TRIP_STATUS_OPTIONS,
    visible: false,
};

/**
 * The Pick, Del and Current grids.
 *
 * All three are the same grid over the same DTO - only the store, the grid-layout key and the
 * colouring basis differ - so they share one component rather than three near-identical copies
 * (Architecture.md R1). The tab passes what differs as props.
 *
 * Columns come from TOP_BOARD_COLUMNS in ./DispatchObjectColumns, the dictionary the four bottom
 * FMDISPATCHOBJECT tabs also bind. This used to be a hand-written <Column> block, and being
 * hand-written it was a subset: ninety columns the bottom tabs offered - every user-defined column,
 * the appointment and drayage sets, the revenue figures the Totals panel sums, the audit columns -
 * had no chooser entry here at all, while three the block did carry (ShipperZip, DriverName,
 * DeliveryComment) were missing from the bottom four. One dictionary closes both directions.
 */
const DispatchBoardGrid = (props) => {
    const {
        dataSource,
        accessToken,
        gridKey,
        topTab,
        profile,
        currentLayout,
        setCurrentLayout,
        onFocusedRowChanged,
        onSelectionChanged,
        showTripColumns = false,
    } = props;

    const dataGrid = useRef(null);

    const saveState = useCallback(
        (state) => GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout),
        [gridKey, accessToken, currentLayout]
    );

    const loadState = useCallback(
        () => GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout),
        [gridKey, accessToken, currentLayout]
    );

    const columns = useMemo(
        () => (showTripColumns ? [...TOP_BOARD_COLUMNS, TRIP_STATUS_COLUMN] : TOP_BOARD_COLUMNS),
        [showTripColumns]
    );

    // Rebuilt when the profile changes, because the thresholds and colours it carries are the whole
    // input to the banding decision.
    const onCellPrepared = useMemo(() => buildOnCellPrepared(profile, topTab), [profile, topTab]);

    // Swapping onCellPrepared in does not repaint anything DevExtreme has already rendered - the
    // handler runs per cell as that cell is created - so a grid that has drawn a page keeps the
    // colours it drew it with until something else makes it redraw.
    //
    // BoardTabPane no longer mounts this grid before the profile has resolved, so the case that made
    // the landing tab render uncoloured is gone at the source. This covers the rest: a profile that
    // changes while the board is open, and any future caller that mounts the grid earlier. It is one
    // repaint of the visible rows, not a reload - the store is not touched.
    // `instance()` is a method on the devextreme-react ref, not a property - reading it as a property
    // hands back the wrapper rather than the widget, and the first grid method called on it throws.
    // Every other caller in the repo writes `dataGrid.current.instance()`; see GridLayoutManager.
    useEffect(() => {
        const instance = dataGrid.current?.instance?.();
        if (instance) {
            instance.repaintRows(instance.getVisibleRows().map((row) => row.rowIndex));
        }
    }, [onCellPrepared]);

    return (
        <DataGrid
            ref={dataGrid}
            dataSource={dataSource}
            keyExpr="RowId"
            // Server-side everything. The store is a CustomStore over DataSourceLoader, so paging,
            // sorting, filtering and grouping are all resolved in SQL - this is the largest table the
            // product queries interactively and must never be materialised client-side.
            remoteOperations={true}
            allowColumnReordering={true}
            allowColumnResizing={true}
            columnAutoWidth={true}
            showBorders={true}
            hoverStateEnabled={true}
            height="100%"
            // focusedRowEnabled gives the board a "current row" independent of the selection, which
            // is what the Delphi has: the bottom Details tab follows the row the cursor is on, while
            // Totals sums whatever is ctrl-selected.
            focusedRowEnabled={true}
            onFocusedRowChanged={onFocusedRowChanged}
            onSelectionChanged={onSelectionChanged}
            onCellPrepared={onCellPrepared}
            onExporting={(e) => {
                if (window.handleDevExtremeDataGridExport) {
                    window.handleDevExtremeDataGridExport(e);
                }
            }}
        >
            {/* showScrollbar="always" - see the note in BottomGrid.js. The board's two grids must
                agree on this: a scrollbar that appears in one pane and not the other reads as a
                rendering fault rather than a setting. */}
            <Scrolling mode="standard" showScrollbar="always" />
            <Selection mode="multiple" showCheckBoxesMode="none" />
            <Sorting mode="multiple" />
            {/* Filter row and filter panel only. DEV-6402 also lists a header filter, but it is
                deliberately off on this screen: on a remote-operations grid each header filter
                popup issues its own DISTINCT query against the column, and these grids sit over
                the largest table in the product behind a fifteen-table join. The filter row
                covers the same need without that cost. */}
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={false} />
            <ColumnFixing enabled={true} />
            <Export enabled={true} allowExportSelectedData={true} />
            <Paging defaultPageSize={20} />
            <Pager
                visible={true}
                showPageSizeSelector={true}
                allowedPageSizes={ALLOWED_PAGE_SIZES}
                showInfo={true}
                showNavigationButtons={true}
            />
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
            </ColumnChooser>
            <StateStoring
                enabled={true}
                type="custom"
                storageKey={gridKey}
                customLoad={loadState}
                customSave={saveState}
            />

            {renderColumns(columns)}

            <Toolbar>
                <Item location="after">
                    <GridLayoutManager
                        gridKey={gridKey}
                        dataGrid={dataGrid}
                        setCurrentLayout={setCurrentLayout}
                    />
                </Item>
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
};

export default DispatchBoardGrid;
